/**
 * Search Area Management Utilities
 * Handles creation, deletion, modification of search areas and adjacent area calculations
 */

import { leadGenService } from '../services/leadGeneration';
import toast from 'react-hot-toast';

/**
 * Calculate new position based on direction and distance
 */
export const calculateNewPosition = (lat, lon, direction, distance) => {
  const factor = 1.8;
  const distanceInDegrees = (distance * factor) / 111000;
  
  switch (direction) {
    case 'north':
      return [lat + distanceInDegrees, lon];
    case 'south':
      return [lat - distanceInDegrees, lon];
    case 'east':
      return [lat, lon + (distanceInDegrees / Math.cos(lat * Math.PI / 180))];
    case 'west':
      return [lat, lon - (distanceInDegrees / Math.cos(lat * Math.PI / 180))];
    case 'northeast':
      return [
        lat + (distanceInDegrees * 0.707),
        lon + ((distanceInDegrees * 0.707) / Math.cos(lat * Math.PI / 180))
      ];
    case 'northwest':
      return [
        lat + (distanceInDegrees * 0.707),
        lon - ((distanceInDegrees * 0.707) / Math.cos(lat * Math.PI / 180))
      ];
    case 'southeast':
      return [
        lat - (distanceInDegrees * 0.707),
        lon + ((distanceInDegrees * 0.707) / Math.cos(lat * Math.PI / 180))
      ];
    case 'southwest':
      return [
        lat - (distanceInDegrees * 0.707),
        lon - ((distanceInDegrees * 0.707) / Math.cos(lat * Math.PI / 180))
      ];
    default:
      return [lat, lon];
  }
};

/**
 * Add adjacent area in specified direction
 */
export const addAdjacentArea = async (selectedArea, direction, setUnsearchedAreas, setSelectedArea) => {
  if (!selectedArea) return;

  const radius = selectedArea.radius;
  const lat = parseFloat(selectedArea.latitude);
  const lng = parseFloat(selectedArea.longitude);
  
  const [newLat, newLng] = calculateNewPosition(lat, lng, direction, radius);

  const newArea = {
    latitude: parseFloat(newLat.toFixed(7)),
    longitude: parseFloat(newLng.toFixed(7)),
    radius: radius,
  };

  const { data, error } = await leadGenService.createSearchArea(newArea);
  if (data) {
    setUnsearchedAreas(prev => [...prev, data]);
    setSelectedArea(data);
    toast.success(`Added ${direction} area`);
  } else if (error) {
    toast.error('Failed to add adjacent area');
  }
};

/**
 * Delete selected area
 */
export const deleteSelectedArea = async (selectedArea, setSearchedAreas, setUnsearchedAreas, setSelectedArea) => {
  if (!selectedArea) return;

  const areaToDelete = selectedArea;
  
  if (areaToDelete.has_been_searched) {
    setSearchedAreas(prev => prev.filter(area => area.id !== areaToDelete.id));
  } else {
    setUnsearchedAreas(prev => prev.filter(area => area.id !== areaToDelete.id));
  }
  // Don't clear selection immediately - remove this line
  // setSelectedArea(null);

  const { data, error } = await leadGenService.deleteSearchAreas([areaToDelete.id]);
  if (data) {
    toast.success('Search area deleted');
    // Clear selection only after successful deletion
    setSelectedArea(null);
  } else if (error) {
    console.error('Error deleting search area:', error);
    if (areaToDelete.has_been_searched) {
      setSearchedAreas(prev => [...prev, areaToDelete]);
    } else {
      setUnsearchedAreas(prev => [...prev, areaToDelete]);
    }
    setSelectedArea(areaToDelete);
    toast.error('Failed to delete search area');
  }
};

/**
 * Delete latest unsearched area
 */
export const deleteLatestUnsearchedArea = async (unsearchedAreas, selectedArea, setUnsearchedAreas, setSelectedArea) => {
  if (unsearchedAreas.length === 0) {
    toast.error('No unsearched areas to delete');
    return;
  }

  const latestArea = unsearchedAreas[unsearchedAreas.length - 1];
  
  setUnsearchedAreas(prev => prev.filter(area => area.id !== latestArea.id));
  if (selectedArea && selectedArea.id === latestArea.id) {
    setSelectedArea(null);
  }
  
  const { data, error } = await leadGenService.deleteSearchAreas([latestArea.id]);
  if (data) {
    toast.success('Latest unsearched area deleted');
  } else if (error) {
    console.error('Error deleting latest area:', error);
    setUnsearchedAreas(prev => [...prev, latestArea]);
    toast.error('Failed to delete area');
  }
};

/**
 * Clear all unsearched areas
 */
export const clearUnsearchedAreas = async (unsearchedAreas, setUnsearchedAreas, setSelectedArea, loadSearchAreas, showConfirmation) => {
  if (unsearchedAreas.length === 0) {
    toast.error('No unsearched areas to clear');
    return;
  }

  showConfirmation({
    title: 'Clear Unsearched Areas',
    message: `Delete all ${unsearchedAreas.length} unsearched areas?`,
    type: 'danger',
    confirmText: 'Delete All',
    onConfirm: async () => {
      const areaIds = unsearchedAreas.map(area => area.id);
      
      setUnsearchedAreas([]);
      setSelectedArea(null);
      
      const { data, error } = await leadGenService.deleteSearchAreas(areaIds);
      if (data) {
        toast.success(`Cleared ${areaIds.length} unsearched areas`);
      } else if (error) {
        console.error('Error clearing unsearched areas:', error);
        loadSearchAreas();
        toast.error('Failed to clear unsearched areas');
      }
    }
  });
};

/**
 * Delete latest searched area
 */
export const deleteLatestSearchedArea = async (searchedAreas, selectedArea, setSearchedAreas, setSelectedArea) => {
  if (searchedAreas.length === 0) {
    toast.error('No searched areas to delete');
    return;
  }

  const latestSearchedArea = searchedAreas[searchedAreas.length - 1];

  setSearchedAreas(prev => prev.filter(area => area.id !== latestSearchedArea.id));

  if (selectedArea && selectedArea.id === latestSearchedArea.id) {
    setSelectedArea(null);
  }

  const { data, error } = await leadGenService.deleteSearchAreas([latestSearchedArea.id]);
  if (data) {
    toast.success('Latest searched area deleted');
  } else if (error) {
    console.error('Error deleting searched area:', error);
    setSearchedAreas(prev => [...prev, latestSearchedArea]);
    toast.error('Failed to delete searched area');
  }
};

/**
 * Delete all searched areas
 */
export const deleteAllSearchedAreas = async (searchedAreas, setSearchedAreas, setSelectedArea, loadSearchAreas, showConfirmation) => {
  if (searchedAreas.length === 0) {
    toast.error('No searched areas to delete');
    return;
  }

  showConfirmation({
    title: 'Delete All Searched Areas',
    message: `Delete all ${searchedAreas.length} searched areas permanently?`,
    type: 'danger',
    confirmText: 'Delete All',
    onConfirm: async () => {
      const areaIds = searchedAreas.map(area => area.id);
      
      setSearchedAreas([]);
      setSelectedArea(null);
      
      const { data, error } = await leadGenService.deleteSearchAreas(areaIds);
      if (data) {
        toast.success(`Deleted ${areaIds.length} searched areas`);
      } else if (error) {
        console.error('Error deleting searched areas:', error);
        loadSearchAreas();
        toast.error('Failed to delete searched areas');
      }
    }
  });
};

/**
 * Add included place type
 */
export const addIncludedType = (type, includedTypes, setIncludedTypes) => {
  if (!includedTypes.includes(type)) {
    setIncludedTypes(prev => {
      const newTypes = [...prev, type];
      localStorage.setItem('leadgen_included_types', JSON.stringify(newTypes));
      return newTypes;
    });
  }
};

/**
 * Remove included place type by index
 */
export const removeIncludedType = (index, setIncludedTypes) => {
  setIncludedTypes(prev => {
    const newTypes = prev.filter((_, i) => i !== index);
    localStorage.setItem('leadgen_included_types', JSON.stringify(newTypes));
    return newTypes;
  });
};

/**
 * Add excluded place type
 */
export const addExcludedType = (type, excludedTypes, setExcludedTypes) => {
  if (!excludedTypes.includes(type)) {
    setExcludedTypes(prev => {
      const newTypes = [...prev, type];
      localStorage.setItem('leadgen_excluded_types', JSON.stringify(newTypes));
      return newTypes;
    });
  }
};

/**
 * Remove excluded place type by index
 */
export const removeExcludedType = (index, setExcludedTypes) => {
  setExcludedTypes(prev => {
    const newTypes = prev.filter((_, i) => i !== index);
    localStorage.setItem('leadgen_excluded_types', JSON.stringify(newTypes));
    return newTypes;
  });
};

export const deleteSpecificArea = async (areaId, selectedArea, setUnsearchedAreas, setSelectedArea) => {
  const { data, error } = await leadGenService.deleteSearchArea(areaId);
  if (data) {
    setUnsearchedAreas(prev => prev.filter(area => area.id !== areaId));
    if (selectedArea?.id === areaId) {
      setSelectedArea(null);
    }
    toast.success('Circle deleted');
  } else if (error) {
    toast.error('Failed to delete circle');
  }
};

/**
 * Update filter search mode (category or text)
 */
export const updateFilterSearchMode = (mode, setFilterSearchMode) => {
  setFilterSearchMode(mode);
  localStorage.setItem('leadgen_filter_search_mode', mode);
};

/**
 * Get filter search mode from localStorage
 */
export const getFilterSearchMode = () => {
  const saved = localStorage.getItem('leadgen_filter_search_mode');
  return saved || 'category';
};

/**
 * Update text query
 */
export const updateTextQuery = (query, setTextQuery) => {
  setTextQuery(query);
  localStorage.setItem('leadgen_text_query', query);
};

/**
 * Get text query from localStorage
 */
export const getTextQuery = () => {
  const saved = localStorage.getItem('leadgen_text_query');
  return saved || '';
};